import axios, { AxiosError } from "axios";
import { SeaQueryParams } from "./query.types";
import { SeaResponseDTO } from "./response.types";
import { authInterceptor, sessionRequestConfig } from "shared/api/backofficeClient";
import { injectSearchCustomHeader } from "shared/api/utils/api.utils";
import { mutateMetadataInterceptor } from "./utils";

/**
 * Ocean search goes through the back office rather than straight to the
 * gateway: the back office holds the gateway key and counts guest searches by
 * IP. Air search goes the same way with a counter of its own; the carrier
 * lists still call the gateway directly.
 */
const SEA_SEARCH_API_URL = "/api/tracking/search";

// Signed-in visitors are recognised by the cross-subdomain session cookie,
// which is also what lifts the guest limit. Guests carry no session, so the
// same request works for them untouched.
const requestInstance = axios.create({ ...sessionRequestConfig });
authInterceptor(requestInstance);
injectSearchCustomHeader(requestInstance);

/**
 * A guest who has spent their free searches gets a 429. The panel already
 * knows how to show that (requestLimitReachedWebApp opens the sign-in modal),
 * but only for a resolved response - so this turns it back into one.
 */
requestInstance.interceptors.response.use(
  (response) => response,
  (error: AxiosError<{ requestLimitReachedWebApp?: boolean }>) => {
    if (error.response?.status === 429 && error.response.data?.requestLimitReachedWebApp) {
      return Promise.resolve({
        ...error.response,
        data: { ...error.response.data, data: {} },
      });
    }

    return Promise.reject(error);
  }
);

mutateMetadataInterceptor(requestInstance);

class SeaSearchService {
  public search(params: SeaQueryParams, signal?: AbortSignal): Promise<SeaResponseDTO> {
    const finalParms: SeaQueryParams = {
      route_details: true,
      ...params,
    };

    // GET, like the gateway endpoint behind it: the back office forwards the
    // query onward untouched, so the gateway sees the request it expects.
    return requestInstance.get(SEA_SEARCH_API_URL, {
      params: finalParms,
      ...(signal ? { signal } : {}),
    });
  }
}

const seaSearchService = new SeaSearchService();

export { seaSearchService };
